import React, { useEffect, useState } from "react";
import { Layout, Typography, Row, Col, Card, Progress, Modal } from "antd";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { fetchDashboardRequest } from "../redux/slices/dashboardSlice";
import { useLocation } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Tooltip as RechartsTooltip,
} from "recharts";
const { Header, Content } = Layout;
const { Title } = Typography;
const Dashboard: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { dailyData, vehicleData, loading, error } = useSelector(
    (state: RootState) => state.dashboard
  );
  useEffect(() => {
    dispatch(fetchDashboardRequest());
  }, [dispatch]);
  
  const location = useLocation();
  useEffect(() => {
    dispatch(fetchDashboardRequest());
  }, [dispatch, location]);

  const cardStyle: React.CSSProperties = {
    width: "100%",
    backgroundColor: "#F4F4F4",
    borderRadius: "10px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  };
  const renderPieChart = (data: typeof vehicleData) => {
    const COLORS = ["#FFBB28", "#00C49F", "#FF8042", "#0088FE"];
    return (
      <PieChart width={300} height={300}>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          outerRadius={100}
          label={({ name, value }) => `${name} (${value})`}
          dataKey="value"
        >
          {data.map((entry: any, index: any) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <RechartsTooltip />
        <Legend />
      </PieChart>
    );
  };
  const renderVehicleAccountGraph = (data: typeof vehicleData) => {
    const COLORS = ["#FFBB28", "#00C49F", "#FF8042", "#0088FE"];
    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <RechartsTooltip />
          <Legend />
          <Bar dataKey="value">
            {data.map((entry: any, index: any) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[index % COLORS.length]}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  };
  return (
    <Layout style={{ minHeight: "100vh", overflow: "hidden" }}>
      <Header style={{ backgroundColor: "whitesmoke", padding: "0 " }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Title
            level={3}
            style={{
              fontSize: "35px",
              fontWeight: "bolder",
              color: "black",
              fontFamily: "serif",
              textShadow: "2px 2px 5px rgba(0, 0, 0, 0.3)",
              margin: 0,
            }}
          >
            Vehicle Accounts Dashboard
          </Title>
        </div>
      </Header>
      <br />
      <Layout>
        <Content>
          {loading && <div>Loading dashboard data...</div>}
          {error && <div style={{ color: "red" }}>Error: {error}</div>}
          {!loading && !error && (
            <>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={12} lg={6}>
                  <Card
                    title="Total Vehicles"
                    bordered={false}
                    style={cardStyle}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "150px",
                      }}
                    >
                      <Progress
                        type="circle"
                        percent={100}
                        format={() => "1,200"}
                        strokeColor="#1890FF"
                        strokeWidth={8}
                      />
                    </div>
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={12} lg={6}>
                  <Card
                    title="Inactive Vehicles"
                    bordered={false}
                    style={cardStyle}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "150px",
                      }}
                    >
                      <Progress
                        type="circle"
                        percent={27}
                        format={() => "320"}
                        strokeColor="#FF4D4F"
                        strokeWidth={8}
                      />
                    </div>
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={12} lg={6}>
                  <Card
                    title="Paid Vehicles"
                    bordered={false}
                    style={cardStyle}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "150px",
                      }}
                    >
                      <Progress
                        type="circle"
                        percent={58}
                        format={() => "700"}
                        strokeColor="#52C41A"
                        strokeWidth={8}
                      />
                    </div>
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={12} lg={6}>
                  <Card
                    title="Overdue Vehicles"
                    bordered={false}
                    style={cardStyle}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "150px",
                      }}
                    >
                      <Progress
                        type="circle"
                        percent={15}
                        format={() => "180"}
                        strokeColor="#FA8C16"
                        strokeWidth={8}
                      />
                    </div>
                  </Card>
                </Col>
              </Row>
              <br />
              <br />
              <Row gutter={[16, 16]} style={{ marginTop: 30 }}>
                <Col xs={24} md={14} style={{ backgroundColor: "#F4F4F4" }}>
                  <Card
                    title="Daily Vehicle Accounts"
                    bordered={false}
                    style={{
                      backgroundColor: "#F4F4F4",
                      borderRadius: "10px",
                      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                      padding: 10,
                    }}
                  >
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={dailyData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <RechartsTooltip />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="value"
                          stroke="#8884d8"
                          activeDot={{ r: 8 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Card>
                </Col>
                <Col xs={24} md={10}>
                  <Card
                    title="Vehicle Account Distribution"
                    bordered={false}
                    style={{
                      backgroundColor: "#F4F4F4",
                      borderRadius: "10px",
                      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                      padding: 10,
                      textAlign: "center",
                    }}
                  >
                    {renderPieChart(vehicleData)}
                  </Card>
                </Col>
              </Row>
              <br />
              <br />
              <Row gutter={[16, 16]} style={{ marginTop: 30 }}>
                <Col span={24}>
                  <Card
                    title="Vehicle Account Graph"
                    bordered={false}
                    style={{
                      backgroundColor: "#F4F4F4",
                      borderRadius: "10px",
                      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                      padding: 10,
                    }}
                  >
                    {renderVehicleAccountGraph(vehicleData)}
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </Content>
      </Layout>
    </Layout>
  );
};
export default Dashboard;
